import { Checkbox, Form, Input, InputNumber } from 'antd';

const toMillicores = (value: string) =>
  parseInt(value.replace('m', '') || '0', 10);

// Istio sidecar proxy resources for a size, applied when its clusters are created or woken up.
// CPU and memory limits are checked against the size's own CPU and memory limit.
function IstioFields({
  sizeCpu,
  sizeMemoryLimit,
}: Readonly<{ sizeCpu?: number | null; sizeMemoryLimit?: number | null }>) {
  return (
    <section className="capacity-drawer__section">
      <h3 className="capacity-drawer__heading">Istio proxy</h3>
      <Form.Item name="istioConfigEnabled" valuePropName="checked" noStyle>
        <Checkbox>Use a custom Istio proxy configuration</Checkbox>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, next) =>
          prev.istioConfigEnabled !== next.istioConfigEnabled
        }
      >
        {({ getFieldValue }) =>
          getFieldValue('istioConfigEnabled') ? (
            <div className="capacity-drawer__panel capacity-drawer__grid capacity-drawer__grid--2">
              <Form.Item
                name="istioProxyConcurrency"
                label="Proxy concurrency"
                extra="Worker threads; 0 detects from CPU."
              >
                <InputNumber min={0} max={64} placeholder="2" />
              </Form.Item>
              <span />
              <Form.Item
                name="istioProxyCpuRequest"
                label="CPU request"
                dependencies={['istioProxyCpuLimit']}
                rules={[
                  {
                    pattern: /^\d+m?$/,
                    message: 'Use a format like 100m or 500m',
                  },
                  ({ getFieldValue: get }) => ({
                    validator(_, value) {
                      const limit = get('istioProxyCpuLimit');
                      if (
                        value &&
                        limit &&
                        toMillicores(value) >= toMillicores(limit)
                      ) {
                        return Promise.reject(
                          new Error(
                            'CPU request must be less than the CPU limit',
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder="100m" />
              </Form.Item>
              <Form.Item
                name="istioProxyCpuLimit"
                label="CPU limit"
                rules={[
                  {
                    pattern: /^\d+m?$/,
                    message: 'Use a format like 1000m or 2000m',
                  },
                  {
                    validator(_, value) {
                      if (!value || !sizeCpu) return Promise.resolve();
                      if (toMillicores(value) > sizeCpu * 1000) {
                        return Promise.reject(
                          new Error(
                            `CPU limit can’t exceed the size’s ${sizeCpu} vCPU (${sizeCpu * 1000}m)`,
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="2000m" />
              </Form.Item>
              <Form.Item
                name="istioProxyMemoryRequest"
                label="Memory request"
                dependencies={['istioProxyMemoryLimit']}
                rules={[
                  {
                    pattern: /^\d+(\.\d+)?G$/i,
                    message: 'Use a format like 4G or 8G',
                  },
                  ({ getFieldValue: get }) => ({
                    validator(_, value) {
                      const limit = get('istioProxyMemoryLimit');
                      if (
                        value &&
                        limit &&
                        parseFloat(value) >= parseFloat(limit)
                      ) {
                        return Promise.reject(
                          new Error(
                            'Memory request must be less than the memory limit',
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input placeholder="8G" />
              </Form.Item>
              <Form.Item
                name="istioProxyMemoryLimit"
                label="Memory limit"
                rules={[
                  {
                    pattern: /^\d+(\.\d+)?G$/i,
                    message: 'Use a format like 32G or 64G',
                  },
                  {
                    validator(_, value) {
                      if (!value || !sizeMemoryLimit) return Promise.resolve();
                      if (parseFloat(value) > sizeMemoryLimit) {
                        return Promise.reject(
                          new Error(
                            `Memory limit can’t exceed the size’s ${sizeMemoryLimit} GB limit`,
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="64G" />
              </Form.Item>
            </div>
          ) : null
        }
      </Form.Item>
    </section>
  );
}

export default IstioFields;
